import { NilmConsumptionElement } from '@vae/server/src/ConsumptionAnalyzer/consumptionAnalyzer'
import {
	BarElement,
	CategoryScale,
	ChartData,
	Chart as ChartJS,
	ChartOptions,
	Legend,
	LinearScale,
	Tick,
	Title,
	Tooltip,
} from 'chart.js'
import dayjs from 'dayjs'
import 'dayjs/locale/fi'
import { TFunction } from 'i18next'
import React, { useContext, useRef } from 'react'
import { Chart } from 'react-chartjs-2'
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types'
import { useTranslation } from 'react-i18next'
import { ResourceType } from '../../../@types/i18n'
import { AppContext } from '../../../contexts/AppContext'
import { getFontSize } from '../../../utils/getFontSize'
import { roundTicksTo1Decimal } from '../../electricity/utils/roundTicksTo1Decimal'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

interface ConsumptionElementsChartProps {
	data: NilmConsumptionElement[]
	setActiveElement: React.Dispatch<React.SetStateAction<number>>
	noData: boolean
}

const getResponsiveChartOptions = (isDarkMode: boolean, noData: boolean): ChartOptions<'bar'> => {
	const xTickColor = isDarkMode ? '#e3e3e3' : '#858585'
	const yTickColor = isDarkMode ? '#3cb49f' : '#127d27'
	const gridColor = isDarkMode ? '#144448' : '#f5f5f5'
	const legendLabelColor = isDarkMode ? '#e3e3e3' : '#858585'

	const fontSize = getFontSize()

	return {
		elements: {
			bar: {
				borderRadius: 2,
			},
		},
		maintainAspectRatio: false,
		scales: {
			x: {
				stacked: true,
				grid: {
					display: noData ? true : false,
				},
				display: !noData,
				ticks: {
					color: xTickColor,
					font: {
						size: fontSize,
					},
				},
			},
			y: {
				position: 'left',
				border: {
					display: false,
				},
				grid: {
					color: gridColor,
				},
				stacked: true,
				ticks: {
					color: yTickColor,
					callback(tickValue: string | number, index: number, ticks: Tick[]) {
						return roundTicksTo1Decimal(tickValue, index, ticks)
					},
					display: !noData,
					font: {
						size: fontSize,
					},
				},
				grace: '25%',
			},
		},
		plugins: {
			tooltip: {
				titleFont: {
					size: fontSize + 2,
					weight: 'bold',
				},
				bodyFont: {
					size: fontSize,
				},
				enabled: true,
			},
			legend: {
				labels: {
					color: legendLabelColor,
					font: {
						size: fontSize,
					},
				},
			},
		},
	}
}

const ConsumptionElementsDistributionChart: React.FC<ConsumptionElementsChartProps> = ({
	data,
	setActiveElement,
	noData,
}) => {
	const { isDarkMode } = useContext(AppContext)
	const ref = useRef<ChartJSOrUndefined<any, string[] | number[], any>>(null)
	const { t } = useTranslation('consumptionElements')

	const handleChartClick = (event: any) => {
		if (ref.current) {
			const index = ref.current.scales.x.getValueForPixel(event.nativeEvent.offsetX)

			if (index && index <= 0) setActiveElement(0)
			else if (index && index > data.length - 1) setActiveElement(data.length - 1)
			else setActiveElement(index ?? 0)
		}
	}

	const options = getResponsiveChartOptions(isDarkMode, noData)

	const datasets =
		createChartData(
			data.sort((a, b) => b.kwh - a.kwh),
			t,
			isDarkMode
		) ?? []

	return <Chart ref={ref} type='bar' options={options} data={datasets} onClick={handleChartClick} />
}

export default ConsumptionElementsDistributionChart

function createChartData(
	data: NilmConsumptionElement[],
	t: TFunction<'consumptionElements', undefined>,
	isDarkMode: boolean
): ChartData<any, number[] | string[], any> {
	const labelsSet = new Set<string>()
	const datasets: { label: string; backgroundColor: string; data: number[] }[] = []

	data.forEach(element => {
		const dataset: { label: string; data: number[]; backgroundColor: string } = {
			label: t(element.name as keyof ResourceType['consumptionElements']),
			data: [],
			backgroundColor: '#23DF48',
		}

		element.consumption.forEach(entry => {
			const date = dayjs(entry.utcStartHour).locale('fi').format('DD.MM')
			labelsSet.add(date)
		})

		datasets.push(dataset)
	})

	const labels = Array.from(labelsSet).sort((a, b) => {
		return dayjs(a, 'DD.MM').toDate().getTime() - dayjs(b, 'DD.MM').toDate().getTime()
	})

	datasets.forEach((dataset, i) => {
		const consumptionMap = new Map<string, number>()
		data[i].consumption.forEach(entry => {
			const date = dayjs(entry.utcStartHour).locale('fi').format('DD.MM')
			consumptionMap.set(date, (consumptionMap.get(date) || 0) + Number(entry.kWh.toFixed(2)))
		})
		dataset.data = labels.map(label => consumptionMap.get(label) || 0)
		dataset.backgroundColor = isDarkMode ? darkColors[i] : lightColors[i]
	})

	return { labels, datasets }
}

const lightColors = [
	'#52ae32', // Green
	'#2196F3', // Blue
	'#FFEB3B', // Yellow
	'#FF9800', // Orange
	'#673AB7', // Purple
	'#00BCD4', // Cyan
	'#8BC34A', // Light Green
	'#FF5722', // Deep Orange
	'#607D8B', // Grayish Blue
	'#795548', // Brown
]

const darkColors = [
	'#3cb49f', // Dark Green
	'#1565C0', // Dark Blue
	'#FBC02D', // Dark Yellow
	'#E65100', // Dark Orange
	'#4527A0', // Dark Purple
	'#00838F', // Dark Cyan
	'#558B2F', // Dark Light Green
	'#BF360C', // Darker Orange
	'#455A64', // Darker Grayish Blue
	'#4E342E', // Dark Brown
]
